/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { AxiosError } from 'axios';

/**
 * Standard error response structure from SSO API
 */
export interface SsoErrorResponse {
  code: string;
  message: string;
}

/**
 * Type for login-related Axios errors with proper typing
 */
export type LoginError = AxiosError<SsoErrorResponse> & {
  errorBody?: {
    message?: string;
  };
};

/**
 * Extracts a message from any error shape, preferring what the API said over
 * the transport's own wording.
 *
 * Takes `unknown` rather than `Error` so a rejected value that is not an Error
 * (a string, `undefined`) falls through to the fallback instead of throwing.
 */
export const extractErrorMessage = (
  error: unknown,
  fallbackMessage: string = 'An error occurred. Please try again.',
): string => {
  const raw = error as
    | {
        response?: { data?: Partial<SsoErrorResponse> };
        errorBody?: { message?: string };
        message?: string;
      }
    | null
    | undefined;

  return (
    raw?.response?.data?.message ||
    raw?.errorBody?.message ||
    raw?.message ||
    fallbackMessage
  );
};

export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => {
  const bodyMessage = (
    error as { response?: { data?: Partial<SsoErrorResponse> } } | null
  )?.response?.data?.message;
  return typeof bodyMessage === 'string' && bodyMessage.trim()
    ? bodyMessage
    : fallbackMessage;
};
